import React, { useState, useCallback } from 'react';
import TextBox from 'devextreme-react/text-box';
import { AntLocalization } from 'ante-react-kit';
import AntPickerPopup from '../AntPickerPopup';
import SearchView from './SearchView';
import useEmployeeSearch from './useEmployeeSearch';

function buildPatchFromMapping(row, mapping, dataField) {
    const patch = {};
    if (Array.isArray(mapping) && mapping.length > 0) {
        mapping.forEach((m) => {
            if (!m || !m.from || !m.to) return;
            patch[m.to] = row[m.from] != null ? row[m.from] : null;
        });
    } else if (dataField) {
        patch[dataField] = row.code;
    }
    return patch;
}

function buildClearPatchFromMapping(mapping, dataField) {
    const patch = {};
    if (Array.isArray(mapping) && mapping.length > 0) {
        mapping.forEach((m) => {
            if (!m || !m.to) return;
            patch[m.to] = null;
        });
    } else if (dataField) {
        patch[dataField] = null;
    }
    return patch;
}

export default function EmployeePicker({ dataField, value, formData, options, onValueChanged, disabled, autoOpen, onClosed, headless }) {
    const [open, setOpen] = useState(!!autoOpen);

    const mapping = (options && options.mapping) || null;
    const displayField = (options && (options.formDisplayField || options.displayField)) || null;
    const extraConditions = (options && options.searchConditions) || null;

    const search = useEmployeeSearch({ enabled: open, extraConditions });

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setOpen(true);
    }, [disabled]);

    const handleClose = useCallback(() => {
        setOpen(false);
        search.reset();
        if (onClosed) onClosed();
    }, [search, onClosed]);

    const handleConfirm = useCallback(
        (row) => {
            if (!row) return;
            onValueChanged(buildPatchFromMapping(row, mapping, dataField));
            handleClose();
        },
        [mapping, dataField, onValueChanged, handleClose]
    );

    const handleClear = useCallback(
        (e) => {
            if (e && e.event && e.event.stopPropagation) e.event.stopPropagation();
            if (disabled) return;
            onValueChanged(buildClearPatchFromMapping(mapping, dataField));
        },
        [mapping, dataField, onValueChanged, disabled]
    );

    const displayName = (displayField && formData && formData[displayField]) || value || '';

    return (
        <>
            {!headless && (
                <div className={'epk-trigger' + (disabled ? ' epk-trigger--disabled' : '') + (displayName ? ' epk-trigger--filled' : '')}>
                    <TextBox
                        value={displayName}
                        readOnly={true}
                        disabled={disabled}
                        stylingMode="outlined"
                        showClearButton={!disabled && !!displayName}
                        placeholder={AntLocalization.translateText({ vi: 'Chọn nhân viên...', en: 'Select employee...' })}
                        onFocusIn={handleOpen}
                        onValueChanged={(e) => {
                            if (e && e.value === '' && e.previousValue) handleClear(e);
                        }}
                    />
                </div>
            )}
            <AntPickerPopup
                visible={open}
                onClose={handleClose}
                title={AntLocalization.translateText({ vi: 'Chọn nhân viên', en: 'Select Employee' })}
            >
                <SearchView
                    query={search.query}
                    onQueryChange={search.setQuery}
                    rows={search.rows}
                    totalCount={search.totalCount}
                    loading={search.loading}
                    loadMore={search.loadMore}
                    onPickCard={handleConfirm}
                    activeCode={value}
                />
            </AntPickerPopup>
        </>
    );
}
